// InputField.jsx - Versión corregida con verificación de seguridad

import React from 'react';
import PropTypes from 'prop-types';
import { useMediaQuery, useTheme, Box } from '@mui/material';
import { X, ArrowUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getStyleComponent } from '../styles';
import {
  StyledTextField,
  SendButton,
  ClearButton,
  InputContainer
} from '../HomeInputStyles';
import AppleSpinner from '../../../common/AppleSpinner';
// Importar el nuevo componente de sugerencias
import InputSuggestions from './InputSuggestions';

const InputField = ({
  state,
  isWaiting,
  inputRef,
  onSubmit,
  onStateUpdate,
  onSuggestionsUpdate,
  interfaceStyle,
  onSuggestionClick = (action) => {
    console.warn('onSuggestionClick not provided, received action:', action);
  } // Valor por defecto usando ES6 en lugar de defaultProps
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();

  // Obtener estilos del componente de estilo correspondiente
  const styleComponent = getStyleComponent(interfaceStyle);
  const styleOverrides = styleComponent.getTextFieldStyles(theme);

  const handleInputChange = React.useCallback((event, newValue) => {
    onStateUpdate({ input: newValue || '' });
    onSuggestionsUpdate(newValue);
  }, [onStateUpdate, onSuggestionsUpdate]);

  const handleClear = React.useCallback(() => {
    onStateUpdate({
      input: '',
      showTrending: false,
      showSuggestions: false,
      filteredSuggestions: []
    });
  }, [onStateUpdate]);

  const handleKeyDown = React.useCallback((event) => {
    if (event.key === 'Enter') {
      if (isMobile) {
        if (event.shiftKey) {
          event.preventDefault();
          onSubmit(event);
        }
      } else {
        if (!event.shiftKey) {
          event.preventDefault();
          onSubmit(event);
        }
      }
    }
  }, [isMobile, onSubmit]);

  // ✅ CORREGIDO: Nuevo handler específico para las sugerencias de AGT con verificación de seguridad
  const handleAGTSuggestionClick = React.useCallback((action) => {
    // Verificación de seguridad
    if (typeof onSuggestionClick !== 'function') {
      console.error('onSuggestionClick is not a function. Received:', typeof onSuggestionClick, onSuggestionClick);
      console.error('InputField props:', { onSuggestionClick, state, isWaiting, interfaceStyle });
      return;
    }

    // Limpiar el input primero
    onStateUpdate({
      input: '',
      showTrending: false,
      showSuggestions: false,
      filteredSuggestions: []
    });

    // Llamar al handler del componente padre
    try {
      onSuggestionClick(action);
    } catch (error) {
      console.error('Error calling onSuggestionClick:', error);
    }
  }, [onStateUpdate, onSuggestionClick, state, isWaiting, interfaceStyle]);

  return (
    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', position: 'relative' }}>
      <InputContainer sx={{ maxWidth: '800px', margin: '0 auto', position: 'relative' }}>
        {/* Solo renderizar InputSuggestions si onSuggestionClick está disponible */}
        {typeof onSuggestionClick === 'function' && (
          <InputSuggestions 
            inputValue={state.input} 
            onSuggestionClick={handleAGTSuggestionClick} 
          />
        )}
        
        <StyledTextField
          inputRef={inputRef}
          placeholder={t('homeInput.placeholder')}
          variant="outlined"
          fullWidth
          multiline
          maxRows={10}
          disabled={isWaiting}
          value={state.input}
          onChange={(e) => handleInputChange(e, e.target.value)}
          onKeyDown={handleKeyDown}
          sx={styleOverrides}
          InputProps={{
            endAdornment: (
              <>
                {state.input && !isWaiting && (
                  <ClearButton
                    onClick={handleClear}
                    size="small"
                    aria-label="clear"
                  >
                    <X size={20} />
                  </ClearButton>
                )}
                <SendButton
                  onClick={onSubmit}
                  disabled={isWaiting || !state.input.trim()}
                  size="small"
                  aria-label="send"
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center' 
                  }}
                >
                  {isWaiting ? (
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      position: 'relative',
                      top: '-6px'
                    }}>
                      <AppleSpinner sizeDesktop={24} sizeMobile={20} />
                    </Box>
                  ) : (
                    <ArrowUp size={24} />
                  )}
                </SendButton>
              </>
            ),
          }}
        />
      </InputContainer>
    </Box>
  );
};

// ✅ CORREGIDO: Hacer onSuggestionClick opcional con valor por defecto
InputField.propTypes = {
  state: PropTypes.shape({
    input: PropTypes.string.isRequired,
    showTrending: PropTypes.bool.isRequired,
    showSuggestions: PropTypes.bool.isRequired,
    filteredSuggestions: PropTypes.array.isRequired,
  }).isRequired,
  isWaiting: PropTypes.bool.isRequired,
  inputRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any })
  ]).isRequired,
  onSubmit: PropTypes.func.isRequired,
  onStateUpdate: PropTypes.func.isRequired,
  onSuggestionsUpdate: PropTypes.func.isRequired,
  interfaceStyle: PropTypes.string.isRequired,
  onSuggestionClick: PropTypes.func, // Opcional - valor por defecto definido en parámetros
};

export default React.memo(InputField);